import { Autocomplete, Divider, TextField } from "@mui/material";
import { styled } from "@mui/material/styles";
import { StyledTableCell } from "../../../components/UXDataTable";
import { convertCelsiusToFahrenheit, convertFlashPoint, formatMidasNumber, getGlobalHyperLinkColor } from "../../../global";

export const autoCompleteFontSize = 16;
export const sampleGridFontSize = 12;

export const StyledDivider = styled(Divider)({
  marginTop: "10px",
});

export const StyledDiv = styled("div")({
  display: "flex",
  marginTop: "5px",
});

export const StyledTextField = styled(TextField)({
  width: "100%",
});

export const StyledTableCellSmall = styled(StyledTableCell)({
  width: 30,
  maxWidth: 60
});

export const StyledTableCellMedium = styled(StyledTableCell)({
  width: 130,
});

export const StyledTableCellLarge = styled(StyledTableCell)({
  width: 260,
});

export const StyledAutocomplete = styled(Autocomplete)({
  "& .MuiAutocomplete-input, & .MuiInputLabel-root": {
    fontSize: autoCompleteFontSize,
  },
  width: "15%",
  marginRight: "15px",
  marginBottom: "20px",
});

export const Title = styled("p")({
  fontFamily: "EMprint",
  fontWeight: "600",
  fontSize: "22px",
  color: "#545459",
  textTransform: "none",
  textAlign: "left",
});

export const SubTitle = styled("p")({
  fontFamily: "EMprint",
  fontWeight: "600",
  fontSize: "16px",
  color: "#545459",
  textTransform: "none",
  textAlign: "left",
  paddingTop: "10px",
});

export const SubSectionTitle = styled("p")({
  fontFamily: "EMprint",
  fontWeight: "600",
  fontSize: "16px",
  color: "#545459",
  textTransform: "none",
  textAlign: "left",
  paddingTop: "20px",
});

export const defaultWorkRequest = Object.freeze({
  statusName: "Draft",
  description: "",
  requestedByEmail: "",
  createdByEmail: null,
  createdDate: null,
  lastModifiedByEmail: null,
  lastModifiedDate: null,
  requestedCompletionDate: null,
  lastAcceptableCompletionDate: null,
  assignedToEmail: "",
  assignedDate: null,
  completedDate: null,
  completedByEmail: null,
  priorityName: "Normal",
  priorityReason: "",
  businessUnitName: null,
  comments: "",
  facilityName: null,
  isNonChemical: false,
  chargeCode: "",
  submissionDate: null,
  hasContainer: true,
  typeName: "",
  siteName: "",
});

export const defaultErrorsInformation = Object.freeze({
  description: null,
  // businessUnitName: null,
  typeName: null,
  requestedCompletionDate: null,
  // lastAcceptableCompletionDate: null,
  requestedByEmail: null,
  siteName: null,
  facilityName: null,
  chargeCode: null,
})

export const defaultShippingAddress = Object.freeze({
  recipientName: '',
  company: '',
  department: '',
  address1: '',
  city: '',
  state: '',
  country: '',
  postalCode: '',
  phoneNumber1: '',
  emailAddress: '',
});

export const defaultShippingRequest = Object.freeze({
  requestedDeliveryDate: null,
  shippingCarrierID: null,
  shippingAddress: null,
  is3PTesting: false,
  shippingReasonName: null,
  isPrepaid: false,
  isAcknowledged: false,
  isTSCAReview: false,
  shippingItems: [],
  containsSamples: false,
  siteSelection: null,
  printBlindCode: false,
  numberOfItems: 0
});

export const defaultErrorsShippingInformation = Object.freeze({
  recipientName: null,
  phoneNumber1: null,
  country: null,
  postalCode: null,
  state: null,
  city: null,
  address1: null,
  requestedDeliveryDate: null,
});

export const defaultErrorsShippingInformationSiteToSite = Object.freeze({
  recipientName: null,
  requestedDeliveryDate: null,
  siteSelection: null,
});

export const defaultShippingItem = Object.freeze({
  thisRequest: null,
  containerID: null,
  thisContainer: null,
  description: null,
  dotHazardClass: null,
  exportValue: null,
  flashPointInC: null,
  quantity: null,
  quantityUoMName: null,
  quantityUoM: null,
  comments: null,
  sampleName: null,
  blindCodeDescription: null,
});

export const newContainerErrorChecks = Object.freeze({
  containerType: false,
  containerStatus: false,
  size: false,
  currentAmount: false,
  uom: false,
  location: false,
  ownerEmail: false,
});

export const labelProp: Record<string, string> = {
  description: 'Description',
  siteName: 'Site',
  siteSelection: 'Site',
  facilityName: 'Facility',
  typeName: 'Request Type',
  businessUnitName: 'Business Unit',
  requestedCompletionDate: 'Required Completion Date',
  lastAcceptableCompletionDate: 'Last Acceptable Date',
  requestedByEmail: 'Requester Email',
  priorityReason: 'Priority Reason',
  assignedToEmail: 'Assigned To',
  chargeCode: 'JADE Number',
  recipientName: 'Recipient\'s Name',
  phoneNumber1: 'Phone #',
  country: 'Country',
  postalCode: 'Zip',
  state: 'State',
  city: 'City',
  address1: 'Street Address',
  requestedDeliveryDate: 'Delivery Required By',
  shippingCarrierID: 'Ship Via',
  shippingReasonName: 'Reason for Shipment',
  isPrepaid: 'Shipment Payment',
  statusName: 'Status',
}

export const CONTAINERING_METHOD_ENUM = {
  MIDAS: "midas",
  NON_SPLIT: "nonsplit",
};

export const RECOMMENDATIONS_ERROR_MESSAGES = {
  NO_CONTAINERS: 'No containers found. Please hit "Get Recommendations" button',
  INVALID_AMOUNT:
    "Invalid amount available. Container does not have enough volume for all tests.",
  REASK_RECOMMENDATIONS:
    "Container and/or Methods has been changed. Please get new recommendations.",
};

// Helper to generate unique temporary IDs for new WorkRequestSamples
let tempIdCounter = -1;
export const generateTempId = () => {
  return `temp_${tempIdCounter--}`;
};

// Get the unique key of a WorkRequestSample (uses backend id or tempId)
export const getWorkRequestSampleKey = (sample: any): string => {
  if (sample.id) return sample.id.toString();
  if (sample.tempId) return sample.tempId.toString();
  return 'unknown';
};

// Ensure the sample has a unique identifier (id or tempId)
export const ensureSampleHasUniqueId = (sample: any) => {
  if (!sample.id && !sample.tempId) {
    return { ...sample, tempId: generateTempId() };
  }
  return sample;
};

export const getFacilitiesExternal = (samples: any[], testSubmissionJSON?: string) => {
  const facilitiesExternal: any[] = [];

  samples.forEach(sample => {
    const json = testSubmissionJSON ?? sample.testSubmissionJSON;
    const sampleFinal = sample.sample ?? sample;
    if (sampleFinal && sampleFinal.containers) {
      const container = sampleFinal.containers.find((f: any) => f.id === sample.existingContainerID);
      const containerNumber = container?.containerNumber ?? 'Any';
      if (json) {
        const parsedValue = JSON.parse(json);
        const methodsExternal = parsedValue.methods.filter((m: any) => m.externalLaboratory);

        if (methodsExternal.length) {
          methodsExternal.forEach((external: any) => {
            facilitiesExternal.push({
              sampleName: formatMidasNumber(sampleFinal.sampleName),
              keyContainer: `${sampleFinal.sampleName}||${containerNumber}`,
              containerNumber,
              existingContainerID: sample.existingContainerID,
              method: external?.method?.name,
              methodDescription: external?.method?.description,
              facility: external?.methodFacility?.testFacilityAbv,
              workRequestId: null,
              laboratoryName: external?.externalLaboratory?.laboratoryName,
              address: external?.externalLaboratory?.shippingAddress ?
                `${external?.externalLaboratory?.shippingAddress?.address1} ${external?.externalLaboratory?.shippingAddress?.city}, ${external?.externalLaboratory?.shippingAddress?.state}`
                : 'N/A',
            })
          })
        }
      }
    }
  })

  const groupFacilitiesExternal = [...facilitiesExternal.reduce((a, b) => {
    const key = `${b.laboratoryName}||${b.address}`;
    if (!a.has(key)) {
      a.set(key, { ...b });
    } else {
      const prevSampleNames = a.get(key).keyContainer.split('\n');
      if (!prevSampleNames.includes(b.keyContainer)) {
        a.get(key).sampleName += `\n${b.sampleName}`;
        a.get(key).containerNumber += `\n${b.containerNumber ?? "Any"}`;
        a.get(key).keyContainer += `\n${b.keyContainer}`;
      }
    }
    return a;
  }, new Map()).values()]
  return groupFacilitiesExternal;
}

export const statusToDisableForm = [
  'Completed',
  'Cancelled',
];

export const statusAllowedForSubmissionForm = [
  'Draft',
  'OnHold',
  'Submitted',
];

/**
 * Copy function for work requests with specific rules:
 * - Copies all General Information (dates cleared)
 * - Copies all Container Info
 * - Copies all Test information
 * - Copies all Shipping information
 * - Converts unavailable containers to ANY
 * - Appends "(Copy of WR ID XXXXXX)" to description
 * - Comments are NOT copied
 * - Status is set to DRAFT
 */
export const copyWorkRequest = (workRequestToCopy: any): any => {
  if (!workRequestToCopy) return null;

  const originalWorkRequestId = workRequestToCopy.id;

  // Create a deep copy to avoid mutating original
  const workRequestCopy = JSON.parse(JSON.stringify(workRequestToCopy));

  // Clear all IDs to create a new record
  delete workRequestCopy.id;
  delete workRequestCopy.specialRequest?.id;
  delete workRequestCopy.specialRequest?.workRequestID;

  // Build the new description with copy reference
  const currentDescription = workRequestCopy.description || '';
  const hasCopySuffix = /\(Copy of WR ID [^)]+\)\s*$/.test(currentDescription);
  const copyReference = originalWorkRequestId ? `(Copy of WR ID ${originalWorkRequestId})` : null;
  const newDescription = copyReference && !hasCopySuffix
    ? (currentDescription ? `${currentDescription} ${copyReference}` : copyReference)
    : currentDescription;

  // Process work request samples - keep all info but clean IDs
  const processedSamples = (workRequestCopy.workRequestSamples || [])
    .filter((wrSample: any) => wrSample?.isRequestContainer !== false)
    .map((wrSample: any) => {
    const processedSample = { ...wrSample };
    
    // Remove IDs
    delete processedSample.id;
    delete processedSample.workRequestID;
    // Process-only fields must not exist in copied drafts.
    delete processedSample.newContainerID;
    delete processedSample.newContainer;
    delete processedSample.newContainers;

    // Container must still be Confirmed/selectable, otherwise fall back to "Any".
    const originalContainer = processedSample.sample?.containers?.find(
      (c: any) => c.id === processedSample.existingContainerID
    );
    if (processedSample.existingContainerID && originalContainer?.containerStatusName !== 'Confirmed') {
      processedSample.existingContainerID = null;
      delete processedSample.existingContainer;
    }

    // Keep test submission JSON (includes test information)
    // Keep all other sample information
    
    return processedSample;
  }).map((sample: any) => ({
    ...sample,
    isRequestContainer: sample?.isRequestContainer ?? true,
  }));

  // Process shipping request if present
  let processedShippingRequest = null;
  if (workRequestCopy.shippingRequest && Object.keys(workRequestCopy.shippingRequest).length > 0) {
    const shippingReq = JSON.parse(JSON.stringify(workRequestCopy.shippingRequest));
    
    // Clear shipping request IDs
    delete shippingReq.id;
    delete shippingReq.workRequestID;
    delete shippingReq.shippingAddress?.id;
    delete shippingReq.tscaReview;
    
    // Clear shipping dates
    shippingReq.requestedDeliveryDate = defaultShippingRequest.requestedDeliveryDate;
    shippingReq.shippingTrackings = [];
    
    // Process shipping items - keep all info but clear IDs
    if (shippingReq.shippingItems && Array.isArray(shippingReq.shippingItems)) {
      shippingReq.shippingItems = shippingReq.shippingItems.map((item: any) => {
        const processedItem = { ...item };
        delete processedItem.id;
        delete processedItem.shippingRequestID;
        
        // Keep container and sample references
        // Keep description and other info
        return processedItem;
      });
    }
    
    processedShippingRequest = shippingReq;
  }

  // Build final work request
  const copiedWorkRequest = {
    ...workRequestCopy,
    // General Information - keep most fields but clear dates
    description: newDescription,
    statusName: defaultWorkRequest.statusName, // Set to DRAFT
    createdByEmail: defaultWorkRequest.createdByEmail,
    createdDate: null,
    lastModifiedByEmail: defaultWorkRequest.lastModifiedByEmail,
    lastModifiedDate: defaultWorkRequest.lastModifiedDate,
    // A copy is never already submitted
    submissionDate: null,
    // Clear request completion dates
    requestedCompletionDate: defaultWorkRequest.requestedCompletionDate,
    lastAcceptableCompletionDate: defaultWorkRequest.lastAcceptableCompletionDate,
    // Clear assignment
    assignedToEmail: null,
    assignedDate: null,
    // Clear completion info
    completedDate: defaultWorkRequest.completedDate,
    completedByEmail: defaultWorkRequest.completedByEmail,
    // DO NOT copy comments
    comments: null,
    // Keep container and test information
    workRequestSamples: processedSamples,
    // Copy shipping information
    shippingRequest: processedShippingRequest,
  };

  return copiedWorkRequest;
};

/**
 * Flattens a copyWorkRequest() result into the shape newCreateWorkRequest expects
 * (nested sample/container objects aren't accepted by the create endpoint).
 */
export const sanitizeWorkRequestForCreate = (copiedWorkRequest: any): any => {
  if (!copiedWorkRequest) return copiedWorkRequest;

  const workRequestSamples = (copiedWorkRequest.workRequestSamples || []).map((wrSample: any) => {
    const { sample, unitOfMeasure, location, container, existingContainer, tempId, ...rest } = wrSample;
    return {
      ...rest,
      sampleName: sample?.sampleName ?? wrSample.sampleName,
    };
  });

  const shippingRequest = copiedWorkRequest.shippingRequest ? {
    ...copiedWorkRequest.shippingRequest,
    shippingItems: (copiedWorkRequest.shippingRequest.shippingItems || []).map((item: any) => {
      const { sample, workRequestSampleKey, ...rest } = item;
      return rest;
    }),
  } : null;

  return {
    ...copiedWorkRequest,
    workRequestSamples,
    shippingRequest,
  };
};

export const copyShippingRequest = (shippingRequestToCopy: any) => {
  delete shippingRequestToCopy.id;
  delete shippingRequestToCopy.workRequestID;
  delete shippingRequestToCopy.shippingAddress?.id;
  delete shippingRequestToCopy.tscaReview;
  const shippingRequest = {
    ...shippingRequestToCopy,
    shippingItems: shippingRequestToCopy.shippingItems.map((item: any, index: number) => {
      delete item.id;
      delete item.shippingRequestID;
      return {
        ...item,
        flashPointInC: convertFlashPoint(convertCelsiusToFahrenheit, item.flashPointInC)
      }
    }),
    requestedDeliveryDate: defaultShippingRequest.requestedDeliveryDate,
    shippingTrackings: []
  }
  return shippingRequest;
}

export const FILE_ATTACHMENT_TABLE_COLUMNS = [
  "File Name",
  "Send with Shipment",
  "File is SDS",
];

export const USALabelInShippingAddress = [
  ' USA',
  'U.S.A.',
  'UNITED',
  'UNITED STATES',
  'United States of America',
  'Unites States',
  'US',
  'USA',
  'USA`',
]

export const IsInternationalShipping = (address: any) => address?.country ? !USALabelInShippingAddress.includes(address.country) : false;

export const DateRequiredMessageByRequestType: Record<string, string> = {
  'Container Registration w/ Tests': 'Required date for splits to be made and dropped off with testing.  Does not include testing time.',
  'Shipping': 'Required date for items to be shipped off-site. Does not include 3rd party shipping time.',
}

export const Option = styled("li")({
  fontSize: sampleGridFontSize,
});

export const NonChecmicalItemCols = ["Description", "Qty", "UoM"];


export enum WORK_REQUEST_TYPES_ENUM {
  ON_SITE_TRANSPORT = "On-site Transport",
  SHIPPING = "Shipping",
  SPECIAL_ANALYSIS = "Special Analysis",
  CONTAINER_REGISTRATION = "Container Registration",
  CONTAINER_REGISTRATION_TESTS = "Container Registration w/ Tests",
  MISCELLANEOUS_REQUEST = "Miscellaneous Request",
  EXXON_MOBIL_SITE_TO_SITE_TRANSPORT = "ExxonMobil Site-to-Site Shuttle",
}

// Types that allow duplicate container selection
export const TYPES_ALLOW_DUPLICATE_CONTAINERS = [
  WORK_REQUEST_TYPES_ENUM.SHIPPING,
  WORK_REQUEST_TYPES_ENUM.EXXON_MOBIL_SITE_TO_SITE_TRANSPORT,
  WORK_REQUEST_TYPES_ENUM.CONTAINER_REGISTRATION
];

// Helper function to check if a type allows duplicate containers
export const allowsDuplicateContainers = (typeName: string): boolean => {
  return TYPES_ALLOW_DUPLICATE_CONTAINERS.includes(typeName as WORK_REQUEST_TYPES_ENUM);
};

// Function to get columns by request type
export const getColumnsByRequestType = (typeName: string, isProcess: boolean = false): string[] => {
  switch (typeName) {
    case WORK_REQUEST_TYPES_ENUM.CONTAINER_REGISTRATION:
      return [
        'midas',
        'container',
        'uom',
        'originalAmount',
        'requestedAmount',
        'currentAmount',
        'description',
        'location',
      ];
    case WORK_REQUEST_TYPES_ENUM.CONTAINER_REGISTRATION_TESTS:
      return [
        'midas',
        'container',
        'uom',
        'originalAmount',
        // 'requestedAmount',
        'currentAmount',
        'returnLocation',
        'blindCodeDescription',
        'description',
      ];
    case WORK_REQUEST_TYPES_ENUM.ON_SITE_TRANSPORT:
      return [
        'midas',
        'container',
        'location',
        ...(isProcess ? ['subLocation'] : []),
        'description',
      ];
    case WORK_REQUEST_TYPES_ENUM.SHIPPING:
      return [
        'midas',
        'container',
        'psimsid',
        'uom',
        'originalAmount',
        'requestedAmount',
        'currentAmount',
        'blindCodeDescription',
        'description',
      ];
    case WORK_REQUEST_TYPES_ENUM.EXXON_MOBIL_SITE_TO_SITE_TRANSPORT:
      return [
        'midas',
        'container',
        'psimsid',
        'uom',
        'originalAmount',
        'requestedAmount',
        'currentAmount',
        'blindCodeDescription',
        'description',
      ];
    case WORK_REQUEST_TYPES_ENUM.MISCELLANEOUS_REQUEST:
      return [
        'midas',
        'container',
        'uom',
        'originalAmount',
        'requestedAmount',
        'currentAmount',
        'description',
        'returnLocation',
      ];
    default:
      return [
        'midas',
        'container',
        'uom',
        'originalAmount',
        'requestedAmount',
        'currentAmount',
        'location',
        ...(isProcess ? ['subLocation'] : []),
        'blindCodeDescription',
        'description',
      ];
  }
};

export const mappingSiteFacilityExternal: Record<string, string> = {
  'Clinton Technical Center': 'Clinton Shipping',
  'Baytown Technology and Engineering Complex': 'BTEC Shipping',
  'Friendswood': 'Friendswood Shipping',
  'Houston Campus': 'Houston Campus Shipping',
}

export const Description = styled("p")({
  fontFamily: "EMprint",
  fontWeight: "600",
  color: "#545459",
  textTransform: "none",
  textAlign: "left",
  fontSize: "14px",
  paddingRight: "20px",
});

export const DescriptionLink = styled("span")({
  fontFamily: "EMprint",
  fontWeight: "600",
  textAlign: "left",
  fontSize: "14px",
  paddingRight: "20px",
  textTransform: "none",
  color: getGlobalHyperLinkColor(),
  textDecoration: "underline",
  cursor: "pointer",
});